import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type MappedAssetSensor, mapAssetSensor, SensorTypeCode } from '@/common/asset-sensor';
import { ROLE } from '@/common/role';
import { timeseriesAggregationTypeLabelsArray } from '@/common/sensors/common';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePermission } from '@/hooks/use-permission';
import { cn, formatDateTime } from '@/lib/utils';
import { useQueryListAssetSensorsOptions } from '@/queries/assets/sensors/list-sensors';

import { AccessDeniedView } from '../../AccessDeniedView';
import { TpsTimeSeriesVectorCharts, type TpsTimeSeriesVectorChartsFilters } from './TimeSeriesVectorCharts';

export type TpsTimeSeriesVectorProps = {
	assetId: string;
	selectedSensor: MappedAssetSensor;
};

const defaultFilters: TpsTimeSeriesVectorChartsFilters = {
	startDate: undefined,
	endDate: undefined,
	meteoSensor: 'none',
	aggregation: 'none',
	geglattet: true,
	delta: true,
};
export function TpsTimeSeriesVector({ assetId, selectedSensor }: TpsTimeSeriesVectorProps) {
	const { t } = useTranslation();
	const [filters, setFilters] = useState<TpsTimeSeriesVectorChartsFilters>(defaultFilters);
	const [showFilters, setShowFilters] = useState(false);
	const hasReadPermission = usePermission(ROLE.SENSORS_TPS_TIMESERIES_READ);
	const aggregationOptions = useMemo(() => timeseriesAggregationTypeLabelsArray(t), [t]);

	const { data: sensorsResponse } = useQuery(useQueryListAssetSensorsOptions(assetId));
	const meteoSensors = useMemo(() => {
		const sensors = sensorsResponse?.sensors || [];
		return sensors.map(mapAssetSensor).filter((sensor) => sensor.type === SensorTypeCode.MET);
	}, [sensorsResponse?.sensors]);

	useEffect(() => {
		if (!selectedSensor?.id) {
			return;
		}
		// Set default date range to last 7 days if not set
		if (!filters.startDate || !filters.endDate) {
			const currentDate = new Date();
			setFilters((fil) => ({
				...fil,
				startDate: subDays(currentDate, 7),
				endDate: currentDate,
			}));
		}
	}, [selectedSensor?.id, filters.startDate, filters.endDate]);

	useEffect(() => {
		if (!selectedSensor?.id) {
			return;
		}
		// Disable warning and alarm options when delta is false
		if (!filters.delta) {
			setFilters((fil) => ({
				...fil,
				warnLow: false,
				warnHigh: false,
				alarm: false,
			}));
		}
	}, [selectedSensor?.id, filters.delta]);

	const handleFilterChange =
		<T extends keyof TpsTimeSeriesVectorChartsFilters>(key: T) =>
		(value: TpsTimeSeriesVectorChartsFilters[T]) => {
			setFilters((prev) => ({
				...prev,
				[key]: value,
			}));
		};

	if (!hasReadPermission) {
		return <AccessDeniedView />;
	}

	return (
		<div className="flex-1 overflow-auto space-y-3">
			<p className="text-sm text-muted-foreground lg:hidden">
				{t('health.sensors.selectedLabel')} <span className="font-medium text-foreground">{selectedSensor.name}</span>
			</p>
			<Card>
				<CardContent className="flex flex-col gap-1">
					{/* Toggle — mobile/tablet only */}
					<div className="flex items-center justify-between lg:hidden">
						<span className="text-xs font-medium text-muted-foreground">{t('health.charts.filters')}</span>
						<button
							type="button"
							onClick={() => setShowFilters((v) => !v)}
							className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
						>
							{showFilters ? t('health.charts.hide') : t('health.charts.show')}
							<ChevronDown
								className={cn('h-3.5 w-3.5 transition-transform duration-300', showFilters && 'rotate-180')}
							/>
						</button>
					</div>

					{/* Collapsible content */}
					<div
						className={cn(
							'grid overflow-hidden transition-all duration-300 ease-in-out lg:grid-rows-[1fr]',
							showFilters ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
						)}
					>
						<div className="overflow-hidden min-h-0 flex flex-col gap-1">
							{/* Satır 1 mobile: Start + End | Satır 2: Meteo + Aggregation */}
							<div className="grid grid-cols-2 lg:grid-cols-[auto_auto_1fr_1fr] gap-1">
								<div>
									<Label className="pb-2 text-xs">{t('health.charts.startDate')}</Label>
									<Popover>
										<PopoverTrigger asChild className="w-full">
											<Button
												variant="outline"
												className={cn(
													'pl-3 text-left font-normal bg-white hover:bg-transparent w-full',
													!filters.startDate && 'text-muted-foreground',
												)}
											>
												{filters.startDate ? (
													formatDateTime(filters.startDate, 'dd-MM-yyyy HH:mm')
												) : (
													<span>{t('health.charts.pickDate')}</span>
												)}
												<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0" align="start">
											<Calendar
												mode="single"
												selected={filters.startDate}
												onSelect={handleFilterChange('startDate')}
												disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
												captionLayout="dropdown"
											/>
										</PopoverContent>
									</Popover>
								</div>

								<div>
									<Label className="pb-2 text-xs">{t('health.charts.endDate')}</Label>
									<Popover>
										<PopoverTrigger asChild className="w-full">
											<Button
												variant="outline"
												className={cn(
													'pl-3 text-left font-normal bg-white hover:bg-transparent w-full',
													!filters.endDate && 'text-muted-foreground',
												)}
											>
												{filters.endDate ? (
													formatDateTime(filters.endDate, 'dd-MM-yyyy HH:mm')
												) : (
													<span>{t('health.charts.pickDate')}</span>
												)}
												<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0" align="start">
											<Calendar
												mode="single"
												selected={filters.endDate}
												onSelect={handleFilterChange('endDate')}
												disabled={(date) => !!filters.startDate && date <= filters.startDate}
												captionLayout="dropdown"
											/>
										</PopoverContent>
									</Popover>
								</div>

								<div>
									<Label className="pb-2 text-xs">{t('health.charts.meteoSensor')}</Label>
									<Select value={filters.meteoSensor} onValueChange={handleFilterChange('meteoSensor')}>
										<SelectTrigger className="w-full bg-white">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-muted" position="popper">
											<SelectItem value="none" className="text-xs">
												{t('health.charts.noneOption')}
											</SelectItem>
											{meteoSensors.map((sensor) => (
												<SelectItem key={sensor.id} value={String(sensor.id)} className="text-xs">
													{sensor.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div>
									<Label className="pb-2 text-xs">{t('health.charts.aggregationLabel')}</Label>
									<Select
										value={filters.aggregation}
										onValueChange={(v) =>
											handleFilterChange('aggregation')(v as TpsTimeSeriesVectorChartsFilters['aggregation'])
										}
									>
										<SelectTrigger className="w-full bg-white">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-muted" position="popper">
											{aggregationOptions.map(({ value, label }) => (
												<SelectItem key={value} value={value} className="text-xs">
													{label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Satır 3 mobile: Geglattet + Delta */}
							<div className="grid grid-cols-2 gap-1 border p-2 rounded-lg">
								<Field orientation="horizontal" className="w-fit">
									<Switch checked={filters.geglattet} onCheckedChange={handleFilterChange('geglattet')} />
									<FieldLabel className="text-xs">{t('health.charts.smoothed')}</FieldLabel>
								</Field>
								<Field orientation="horizontal" className="w-fit">
									<Switch checked={filters.delta} onCheckedChange={handleFilterChange('delta')} />
									<FieldLabel className="text-xs">{t('health.charts.deltaFromBaseline')}</FieldLabel>
								</Field>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
			<TpsTimeSeriesVectorCharts assetId={assetId} selectedSensor={selectedSensor} filters={filters} />
		</div>
	);
}
